import { countryConfig, type CountryCode } from '@keel/core';

/**
 * Words and identifiers that depend on the entity's country. AU-only concepts (GST/BAS, ABN, BSB, HIN, SMSF, Medicare)
 * never appear for other countries; anything not listed falls back to the country config or a neutral term.
 */
export interface LocalTerms {
  readonly salesTax: string;
  /** The sales-tax return: "BAS", "VAT return", … */
  readonly salesTaxReturn: string;
  readonly salesTaxReportPath: string;
  /** Business number on contacts and invoices (ABN, NZBN), or null where the VAT number covers it. */
  readonly businessId: string | null;
  readonly bankCode: { readonly label: string; readonly pattern: RegExp; readonly example: string } | null;
  readonly brokerId: string | null;
  readonly smsf: boolean;
  readonly medicare: boolean;
  readonly companyLabel: string;
  readonly cgtTypeHint: string;
}

export const localTerms = (country: string | undefined): LocalTerms => {
  const cfg = countryConfig((country ?? 'AU') as CountryCode);
  switch (country) {
    case 'GB':
      return {
        salesTax: 'VAT', salesTaxReturn: 'VAT return', salesTaxReportPath: '/reports/vat', businessId: null,
        bankCode: { label: 'Sort code', pattern: /^(\d{2}-?\d{2}-?\d{2})?$/, example: '12-34-56' }, brokerId: null, smsf: false, medicare: false,
        companyLabel: 'Limited company', cgtTypeHint: 'Individuals and sole traders pay CGT after the annual exempt amount; companies pay corporation tax on gains.',
      };
    case 'NZ':
      return {
        salesTax: 'GST', salesTaxReturn: 'GST return', salesTaxReportPath: '/reports/gst-bas', businessId: 'NZBN', bankCode: null, brokerId: null,
        smsf: false, medicare: false, companyLabel: 'Company', cgtTypeHint: 'New Zealand has no general capital gains tax; some gains are taxed as income.',
      };
    case 'AU':
    case undefined:
      return {
        salesTax: 'GST', salesTaxReturn: 'BAS', salesTaxReportPath: '/reports/gst-bas', businessId: 'ABN',
        bankCode: { label: 'BSB', pattern: /^(\d{3}-?\d{3})?$/, example: '062-000' }, brokerId: 'HIN or SRN', smsf: true, medicare: true,
        companyLabel: 'Company (Pty Ltd)', cgtTypeHint: 'Drives the CGT discount: individuals & trusts 50 %, super funds 33⅓ %, companies none.',
      };
    default:
      return {
        salesTax: cfg.salesTaxName, salesTaxReturn: `${cfg.salesTaxName} return`, salesTaxReportPath: '/reports/vat', businessId: 'Tax ID',
        bankCode: null, brokerId: null, smsf: false, medicare: false, companyLabel: 'Company', cgtTypeHint: 'Used for tax estimates on gains.',
      };
  }
};
